/**
 * Finance Hooks
 * React Query hooks for financial data
 * Requirements: 16.6, 16.7
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import {
  fetchFinanceSummary,
  exportFinanceCSV,
  type FinancePeriod,
  type FinanceSummary,
} from '@/lib/api';

/**
 * Hook to fetch financial summary
 */
export function useFinanceSummary(params: {
  period?: FinancePeriod;
  date?: string;
}) {
  return useQuery<FinanceSummary, Error>({
    queryKey: ['finance', 'summary', params.period, params.date],
    queryFn: () => fetchFinanceSummary(params),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Hook to export finance CSV
 */
export function useExportFinanceCSV() {
  return useMutation<Blob, Error, { dateFrom: string; dateTo: string }>({
    mutationFn: exportFinanceCSV,
    onSuccess: (blob, variables) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-export-${variables.dateFrom}-${variables.dateTo}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

/**
 * Parse amount string to number
 */
export function parseAmount(amount: string | null | undefined): number {
  if (!amount) return 0;
  return parseFloat(amount) || 0;
}

/**
 * Format period label
 */
export function getPeriodLabel(period: FinancePeriod): string {
  switch (period) {
    case 'day':
      return 'День';
    case 'week':
      return 'Неделя';
    case 'month':
      return 'Месяц';
    default:
      return period;
  }
}

/**
 * Get date range for period
 */
export function getDateRangeForPeriod(
  period: FinancePeriod,
  date: Date = new Date()
): { start: Date; end: Date } {
  const start = new Date(date);
  const end = new Date(date);

  switch (period) {
    case 'day':
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'week':
      start.setDate(date.getDate() - date.getDay());
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    case 'month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      break;
  }

  return { start, end };
}
